import React, { useEffect } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { resolveTrail, writePersistedTrail, parseTrail } from "@/lib/breadcrumbTrail";

/**
 * Generic trail breadcrumb used by Member + Professional dashboards.
 *
 * Renders   Home › A › B › CurrentLabel   where A/B come from `?trail=…` (or
 * the persisted sessionStorage copy for the same `scope` after a hard refresh).
 *
 * Each intermediate hop is mapped via `sections` (an object of
 * `{ id: { label, href } }`) so the component stays agnostic of the dashboard
 * it's hosted in. Hops whose id isn't in the map are silently dropped, this
 * is correct behaviour for sections the current dashboard can't link back to.
 */
const TrailBreadcrumb = ({
  scope,
  current,
  currentId = "",
  homeLabel = "Home",
  homeHref = "/",
  sections = {},
  className = "",
}) => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const urlTrail = searchParams.get("trail") || "";
  const urlFrom = searchParams.get("from") || "";
  const raw = resolveTrail({ urlTrail, urlFrom, scope });
  const hops = parseTrail(raw, currentId)
    .map((id) => ({ id, ...sections[id] }))
    .filter((h) => h.label && h.href);

  // Keep sessionStorage in sync when the URL carries a trail.
  useEffect(() => {
    if (urlTrail) writePersistedTrail(scope, urlTrail);
    else if (urlFrom) writePersistedTrail(scope, urlFrom);
  }, [urlTrail, urlFrom, scope]);

  // Hide entirely on the dashboard root when there's nothing to walk back to.
  if (!current && hops.length === 0) return null;

  return (
    <nav
      className={`md-trail-breadcrumb ${className}`}
      aria-label="Breadcrumb"
      data-testid="trail-breadcrumb"
      data-trail-scope={scope}
      data-trail-location={location.pathname}
    >
      <Link to={homeHref} className="md-trail-breadcrumb-link" data-testid="trail-breadcrumb-home">
        {homeLabel}
      </Link>
      {hops.map((h, idx) => {
        const earlier = hops.slice(0, idx).map((x) => x.id).join(",");
        const qs = earlier ? `${h.href.includes("?") ? "&" : "?"}trail=${earlier}` : "";
        return (
          <React.Fragment key={h.id}>
            <span className="md-trail-breadcrumb-sep" aria-hidden="true">›</span>
            <Link
              to={`${h.href}${qs}`}
              className="md-trail-breadcrumb-link"
              data-testid={`trail-breadcrumb-hop-${h.id}`}
            >
              {h.label}
            </Link>
          </React.Fragment>
        );
      })}
      {current && (
        <>
          <span className="md-trail-breadcrumb-sep" aria-hidden="true">›</span>
          <span className="md-trail-breadcrumb-current" data-testid="trail-breadcrumb-current">
            {current}
          </span>
        </>
      )}
    </nav>
  );
};

export default TrailBreadcrumb;
